import { useEffect, useRef, useState } from "react";
import { BreadSearchInstitution } from "breadcommon";
import { RyeSearchBar } from "../rye/RyeSearchBar";
import { RyeIcon } from "../rye/RyeIcon";
import { buildClasses } from "../utils/buildClasses";
import { RyeSkeleton } from "../rye/RyeSkeleton";
import { InstitutionLogo } from "../common/InstitutionLogo";

interface InstitutionSearchProps {
  searchQuery: string;
  onSearchChange: (text: string) => void;
  shouldFocus: boolean;
  onFocusAck: () => void;
  isLoading: boolean;
  searchResults: BreadSearchInstitution[];
  initialInstitutions: BreadSearchInstitution[];
  onSelectBank: (bank: BreadSearchInstitution) => void;
}

function InstitutionSkeletonRow(): JSX.Element {
  return (
    <div className={buildClasses("flex", "items-center", "p-3", "rounded-xl")}>
      <RyeSkeleton
        shape="circle"
        className={buildClasses("size-12", "flex-shrink-0")}
      />
      <div
        className={buildClasses(
          "ml-3.5",
          "flex",
          "flex-col",
          "flex-grow",
          "gap-2",
        )}
      >
        <RyeSkeleton className={buildClasses("h-3", "w-2/5")} />
        <RyeSkeleton className={buildClasses("h-2.5", "w-1/4")} />
      </div>
    </div>
  );
}

export function InstitutionSearch({
  searchQuery,
  onSearchChange,
  shouldFocus,
  onFocusAck,
  isLoading,
  searchResults,
  initialInstitutions,
  onSelectBank,
}: InstitutionSearchProps): JSX.Element {
  const activeInstitutions =
    searchQuery.trim() !== "" ? searchResults : initialInstitutions;

  const [highlightIndex, setHighlightIndex] = useState<number | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    setHighlightIndex(activeInstitutions.length > 0 ? 0 : null);
  }, [searchQuery, searchResults, initialInstitutions, activeInstitutions]);

  useEffect(() => {
    if (highlightIndex !== null && itemRefs.current[highlightIndex]) {
      itemRefs.current[highlightIndex]?.scrollIntoView({
        block: "nearest",
        inline: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightIndex]);

  const handleArrowDown = () => {
    if (isLoading || activeInstitutions.length === 0) return;
    setHighlightIndex((prev) => {
      if (prev === null || prev === activeInstitutions.length - 1) {
        return 0;
      }
      return prev + 1;
    });
  };

  const handleArrowUp = () => {
    if (isLoading || activeInstitutions.length === 0) return;
    setHighlightIndex((prev) => {
      if (prev === null || prev === 0) {
        return activeInstitutions.length - 1;
      }
      return prev - 1;
    });
  };

  const handleEnterOverride = (): boolean => {
    if (isLoading) return false;
    if (highlightIndex !== null && activeInstitutions[highlightIndex]) {
      onSelectBank(activeInstitutions[highlightIndex]);
      return true;
    }
    return false;
  };

  return (
    <div className={buildClasses("w-[40rem]", "flex", "flex-col")}>
      <RyeSearchBar
        search={onSearchChange}
        placeholderText="Search for your financial institution"
        width="full"
        showExactMatchButton={false}
        highlightWhenActive={true}
        focus={shouldFocus}
        ackFocus={onFocusAck}
        connectedBelow={true}
        variant="outlined"
        addExtraVerticalPadding={true}
        onArrowDown={handleArrowDown}
        onArrowUp={handleArrowUp}
        onEnterOverride={handleEnterOverride}
      />

      <div
        className={buildClasses(
          "flex",
          "flex-col",
          "bg-surface-50",
          "border-tiny",
          "border-t-0",
          "border-on-surface-200",
          "rounded-b-[22px]",
          "shadow-lg",
          "overflow-hidden",
          "p-3",
          "-mt-[1px]",
          "relative",
          "z-10",
        )}
      >
        {isLoading ? (
          <div
            className={buildClasses(
              "flex",
              "flex-col",
              "gap-1",
              "h-[280px]",
              "overflow-y-auto",
              "overscroll-contain",
              "pr-1",
            )}
          >
            {Array.from({ length: 4 }).map((_, index) => (
              <InstitutionSkeletonRow key={index} />
            ))}
          </div>
        ) : searchQuery.trim() !== "" ? (
          searchResults.length === 0 ? (
            <div
              className={buildClasses(
                "flex",
                "items-center",
                "justify-center",
                "h-[280px]",
                "text-sm",
                "text-on-surface-500",
              )}
            >
              No banks found matching "{searchQuery}"
            </div>
          ) : (
            <div
              className={buildClasses(
                "flex",
                "flex-col",
                "gap-1",
                "h-[280px]",
                "overflow-y-auto",
                "overscroll-contain",
                "pr-1",
              )}
            >
              {searchResults.map((bank, index) => (
                <div
                  key={bank.institution_id}
                  ref={(el) => (itemRefs.current[index] = el)}
                  className={buildClasses(
                    "flex",
                    "items-center",
                    "p-3",
                    "rounded-xl",
                    "cursor-pointer",
                    "transition-colors",
                    "duration-150",
                    {
                      if: highlightIndex === index,
                      then: "bg-on-surface-200/50",
                      else: "hover:bg-on-surface-200/50",
                    },
                  )}
                  onClick={() => onSelectBank(bank)}
                >
                  <InstitutionLogo institution={bank} size={48} />
                  <div
                    className={buildClasses(
                      "ml-3.5",
                      "flex",
                      "flex-col",
                      "flex-grow",
                    )}
                  >
                    <div
                      className={buildClasses(
                        "text-sm",
                        "font-medium",
                        "text-on-surface-800",
                      )}
                    >
                      {bank.name}
                    </div>
                    {bank.url && (
                      <div
                        className={buildClasses(
                          "text-[11px]",
                          "text-on-surface-400",
                          "mt-0.5",
                        )}
                      >
                        {bank.url}
                      </div>
                    )}
                  </div>
                  <RyeIcon
                    name="chevron_right"
                    size="sm"
                    className={buildClasses("text-on-surface-300", "ml-auto")}
                  />
                </div>
              ))}
            </div>
          )
        ) : (
          <div
            className={buildClasses(
              "flex",
              "flex-col",
              "gap-1",
              "h-[280px]",
              "overflow-y-auto",
              "overscroll-contain",
              "pr-1",
            )}
          >
            {initialInstitutions.map((bank, index) => (
              <div
                key={bank.institution_id}
                ref={(el) => (itemRefs.current[index] = el)}
                className={buildClasses(
                  "flex",
                  "items-center",
                  "p-3",
                  "rounded-xl",
                  "cursor-pointer",
                  "transition-colors",
                  "duration-150",
                  {
                    if: highlightIndex === index,
                    then: "bg-on-surface-200/50",
                    else: "hover:bg-on-surface-200/50",
                  },
                )}
                onClick={() => onSelectBank(bank)}
              >
                <InstitutionLogo institution={bank} size={48} />
                <div
                  className={buildClasses(
                    "ml-3.5",
                    "flex",
                    "flex-col",
                    "flex-grow",
                  )}
                >
                  <div
                    className={buildClasses(
                      "text-sm",
                      "font-medium",
                      "text-on-surface-800",
                    )}
                  >
                    {bank.name}
                  </div>
                  {bank.url && (
                    <div
                      className={buildClasses(
                        "text-[11px]",
                        "text-on-surface-400",
                        "mt-0.5",
                      )}
                    >
                      {bank.url}
                    </div>
                  )}
                </div>
                <RyeIcon
                  name="chevron_right"
                  size="sm"
                  className={buildClasses("text-on-surface-300", "ml-auto")}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
